import { Injectable, inject, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AgendaRepository } from 'src/app/domain/service/agenda/agenda-repository';
import { CatalogoRepository } from 'src/app/domain/service/catalogo/catalogo-repository';
import { UsuarioStateService } from 'src/app/infrastructure/state/usuario-state.service';
import { IdentityServiceImpl } from 'src/app/infrastructure/adapters/security/identity.service';
import { RECURSO_ADMINISTRAR_OTRAS_AGENDAS } from 'src/app/common/constants-ejb';
import {
  Persona,
  getNombreCompleto
} from 'src/app/domain/model/persona/persona';
import {
  ProgramacionServicio,
  TipoCita,
  ProgramacionServicioRequest
} from 'src/app/domain/model/agenda/agenda';
import { CatalogoOption } from 'src/app/domain/model/catalogo/catalogo';
import {
  SCHEDULING_TOOLTIPS,
  SCHEDULING_MESSAGES
} from 'src/app/common/scheduling-constants';
import { CancelarCitaResult } from '../../cita/components/cancelar-cita-dialog.component';
import { extractHttpErrorMessage } from 'src/app/common/http-error-utils';
import { fechaLocal } from '../components/week-calendar/week-calendar.utils';

/**
 * Facade para Administrar Agenda
 *
 * Contiene toda la lógica de negocio y estado reactivo.
 * El componente solo delega a este servicio.
 *
 * Flujo Legacy:
 * 1. Búsqueda de asesor/consultor (por tipo identificación + número)
 * 2. Seleccionar asesor de la tabla
 * 3. Mostrar datos del asesor seleccionado
 * 4. Botones: Ver Agenda, Programación Masiva
 * 5. Ver Agenda abre modal con calendario semanal
 *
 * @author Migrado por GitHub Copilot - Ingeneo
 */
@Injectable()
export class AdministrarAgendaFacade {
  // === Servicios ===
  private readonly router = inject(Router);
  private readonly messageService = inject(MessageService);
  private readonly agendaRepository = inject(AgendaRepository);
  private readonly catalogoRepository = inject(CatalogoRepository);
  private readonly usuarioState = inject(UsuarioStateService);
  private readonly identityService = inject(IdentityServiceImpl);

  // === Seguridad: Restricción por tipo empleado (Legacy: FilterBaseInterfaceImpl.initBuscadores) ===
  /**
   * Si el usuario es tipo Asesor (CDTIPO_EMP_ASES), no puede buscar otros asesores.
   * Su campo consultor se precarga con sus propios datos y el buscador se oculta.
   */
  readonly esAsesor = this.usuarioState.esAsesor;

  /**
   * Recurso SEUS `administrarOtrasAgendas` (mismo patrón que
   * `rsCumplimientoSinOCMasivo` en Sin OC).
   * `true` = puede buscar y ver agenda de otros asesores.
   * `false` = queda restringido a su propia agenda. También es el valor mientras
   * se consulta SEUS y si la consulta falla (política cerrada).
   */
  private readonly _puedeAdministrarAgendaOtros = signal(false);
  readonly puedeAdministrarAgendaOtros = this._puedeAdministrarAgendaOtros.asReadonly();

  // === Estado Principal ===
  readonly loading = signal(false);
  readonly loadingAgenda = signal(false);
  readonly loadingDetalle = signal(false);
  readonly asesorSeleccionado = signal<Persona | null>(null);

  // === Catálogos ===
  readonly tiposIdentificacion = signal<CatalogoOption[]>([]);
  // Solo ADMIN: usado en modo CREATE. CAMPO/INFORME no se crean desde administración de agenda.
  readonly tiposCitaDropdown = signal<TipoCita[]>([]);
  // ADMIN + SERVICIO: usado en modo VIEW/EDIT para que citas CAMPO(17)/INFORME(18) tengan su tipo en el catálogo.
  readonly tiposCitaCompletos = signal<TipoCita[]>([]);

  // === Estado Diálogos ===
  readonly dialogAgendaVisible = signal(false);
  readonly dialogDetalleCitaVisible = signal(false);

  // === Estado Cita ===
  readonly citaSeleccionada = signal<ProgramacionServicio | null>(null);
  readonly citaMode = signal<'view' | 'edit' | 'create'>('view');
  readonly citasSemana = signal<ProgramacionServicio[]>([]);

  // === Estado Calendario ===
  readonly anioSeleccionado = signal(new Date().getFullYear());
  readonly mesSeleccionado = signal(new Date().getMonth() + 1);
  readonly semanaInicio = signal<Date>(this.getInicioSemana(new Date()));

  // === Constantes para template ===
  readonly tooltips = SCHEDULING_TOOLTIPS;
  readonly messages = SCHEDULING_MESSAGES;

  readonly anios = Array.from(
    { length: 11 },
    (_, i) => new Date().getFullYear() - 5 + i
  );
  readonly meses = [
    { label: 'ENERO', value: 1 },
    { label: 'FEBRERO', value: 2 },
    { label: 'MARZO', value: 3 },
    { label: 'ABRIL', value: 4 },
    { label: 'MAYO', value: 5 },
    { label: 'JUNIO', value: 6 },
    { label: 'JULIO', value: 7 },
    { label: 'AGOSTO', value: 8 },
    { label: 'SEPTIEMBRE', value: 9 },
    { label: 'OCTUBRE', value: 10 },
    { label: 'NOVIEMBRE', value: 11 },
    { label: 'DICIEMBRE', value: 12 }
  ];

  readonly diasSemanaCortos = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];
  readonly horas = Array.from({ length: 24 }, (_, i) => i);

  /** 96 slots de 15 minutos (24h x 4) para la grilla semanal */
  readonly slots: { hora: number; minuto: number }[] = Array.from(
    { length: 96 },
    (_, i) => ({
      hora: Math.floor(i / 4),
      minuto: (i % 4) * 15
    })
  );

  /** Altura en px de cada slot de 15 min */
  readonly SLOT_HEIGHT_PX = 25;

  readonly tiposCitaLeyenda = [
    { label: 'Campo', color: '#31B404' },
    { label: 'Informe', color: '#9F81F7' },
    { label: 'Cita Tentativa', color: '#F87431' },
    { label: 'Bloqueo Program', color: '#ADD8E6' },
    { label: 'Administrativas', color: '#F4FA58' },
    { label: 'Cliente Particular', color: '#1589FF' },
    { label: 'Bloqueo Adscritos', color: '#736F6E' },
    { label: 'EPT Salud', color: '#31B404' }
  ];

  // === Computed ===
  readonly nombreMes = computed(() => {
    const nombres = [
      '',
      'Enero',
      'Febrero',
      'Marzo',
      'Abril',
      'Mayo',
      'Junio',
      'Julio',
      'Agosto',
      'Septiembre',
      'Octubre',
      'Noviembre',
      'Diciembre'
    ];
    return nombres[this.mesSeleccionado()];
  });

  readonly diasVaciosInicio = computed(() => {
    const primerDia = new Date(
      this.anioSeleccionado(),
      this.mesSeleccionado() - 1,
      1
    );
    // Semana inicia el lunes: el domingo (0) ocupa la última columna.
    return Array((primerDia.getDay() + 6) % 7).fill(0);
  });

  readonly diasDelMes = computed(() => {
    const ultimoDia = new Date(
      this.anioSeleccionado(),
      this.mesSeleccionado(),
      0
    ).getDate();
    return Array.from({ length: ultimoDia }, (_, i) => i + 1);
  });

  readonly diasSemanaActual = computed(() => {
    const dias = [];
    const nombresDias = [
      'Domingo',
      'Lunes',
      'Martes',
      'Miercoles',
      'Jueves',
      'Viernes',
      'Sabado'
    ];
    const nombresMeses = [
      'Ene',
      'Feb',
      'Mar',
      'Abr',
      'May',
      'Jun',
      'Jul',
      'Ago',
      'Sep',
      'Oct',
      'Nov',
      'Dic'
    ];
    const inicio = this.semanaInicio();

    for (let i = 0; i < 7; i++) {
      const fecha = new Date(inicio);
      fecha.setDate(fecha.getDate() + i);
      dias.push({
        // Formato local YYYY-MM-DD (no toISOString que convierte a UTC)
        fecha: fechaLocal(fecha),
        nombreDia: nombresDias[fecha.getDay()],
        fechaCorta: `${fecha.getDate()} ${nombresMeses[fecha.getMonth()]}`
      });
    }
    return dias;
  });

  // === Helpers ===
  readonly getNombreCompleto = getNombreCompleto;

  // === Inicialización ===
  async init(): Promise<void> {
    this.semanaInicio.set(this.getInicioSemana(new Date()));
    await Promise.all([
      this.cargarTiposCita(),
      this.cargarTiposIdentificacion(),
      this.precargarAsesorSesion()
    ]);
  }

  /**
   * Si el usuario NO tiene el recurso SEUS `administrarOtrasAgendas`, precarga sus
   * propios datos como asesor seleccionado y el componente oculta el buscador. Quien
   * lo tiene SÍ ve el buscador y no se precarga nada.
   */
  private async precargarAsesorSesion(): Promise<void> {
    try {
      const puedeAdministrarOtras = await this.consultarPermisoAgendaOtros();
      this._puedeAdministrarAgendaOtros.set(puedeAdministrarOtras);
      if (puedeAdministrarOtras) return;

      let usuario = await this.usuarioState.cargar();
      if (!usuario) {
        await new Promise((resolve) => setTimeout(resolve, 200));
        usuario = await this.usuarioState.cargar();
      }
      if (!usuario) return;

      const asesorPrecargado: Persona = {
        cdpersona: usuario.nmconsecutivo || 0,
        cdtipo_identificacion: usuario.tipoIdentificacion || '',
        nmidentificacion: usuario.numeroIdentificacion || '',
        dsnombre1: usuario.primerNombre || '',
        dsnombre2: usuario.segundoNombre || '',
        dsapellido1: usuario.primerApellido || '',
        dsapellido2: usuario.segundoApellido || '',
        dni: usuario.dni || '',
        snactivo: 'S'
      };
      this.asesorSeleccionado.set(asesorPrecargado);
      this.cargarTiposCita(asesorPrecargado.dni);
    } catch (error) {
      if (!environment.production) {
        console.error('Error precargando asesor sesión:', error);
      }
    }
  }

  /** Consulta el recurso SEUS; ante error o respuesta no booleana, sin permiso. */
  private async consultarPermisoAgendaOtros(): Promise<boolean> {
    try {
      const autorizado = await firstValueFrom(
        this.identityService.isAuthorized(RECURSO_ADMINISTRAR_OTRAS_AGENDAS)
      );
      return autorizado === true;
    } catch {
      return false;
    }
  }

  // === Carga de Catálogos ===
  private async cargarTiposIdentificacion(): Promise<void> {
    try {
      const tipos = await this.catalogoRepository.getTiposIdentificacion();
      this.tiposIdentificacion.set(tipos);
    } catch (error) {
      if (!environment.production) {
        console.error('Error cargando tipos de identificación:', error);
      }
    }
  }

  async cargarTiposCita(dniEmpleado?: string): Promise<void> {
    try {
      // ADMIN alimenta el dropdown de CREATE (sin CAMPO/INFORME, esos solo se crean desde OS).
      // SERVICIO se combina con ADMIN en tiposCitaCompletos para editar/ver citas CAMPO(17)/INFORME(18)
      // que en el legacy se filtran por USO_CITA_SERVICIO en ServiceAppointmentInterfaceImpl:1345.
      const [tiposAdmin, tiposServicio] = await Promise.all([
        this.agendaRepository.listarTiposCita(dniEmpleado, 'ADMIN'),
        this.agendaRepository.listarTiposCita(dniEmpleado, 'SERVICIO')
      ]);
      this.tiposCitaDropdown.set(tiposAdmin);
      const mapa = new Map<number, TipoCita>();
      [...tiposAdmin, ...tiposServicio].forEach((t) =>
        mapa.set(t.nmconsecutivo, t)
      );
      this.tiposCitaCompletos.set(Array.from(mapa.values()));
    } catch (error) {
      const fallback: TipoCita[] = [
        { nmconsecutivo: 42, dstipo: 'Campo Complementario' },
        { nmconsecutivo: 43, dstipo: 'Informe Complementario' }
      ];
      this.tiposCitaDropdown.set(fallback);
      this.tiposCitaCompletos.set([
        ...fallback,
        { nmconsecutivo: 17, dstipo: 'Campo' },
        { nmconsecutivo: 18, dstipo: 'Informe' }
      ]);
    }
  }

  // === Selección de Asesor ===

  seleccionarAsesor(asesor: Persona): void {
    this.asesorSeleccionado.set(asesor);

    const dniCompleto =
      asesor?.dni ||
      (asesor?.cdtipo_identificacion && asesor?.nmidentificacion
        ? `${asesor.cdtipo_identificacion}${asesor.nmidentificacion}`
        : undefined);
    this.cargarTiposCita(dniCompleto);

    this.messageService.add({
      severity: 'success',
      summary: 'Consultor seleccionado',
      detail: `${this.getNombreCompleto(asesor)}`
    });
  }

  // === Navegación ===
  abrirProgramacionMasiva(): void {
    this.router.navigate(['/agenda/programacion-masiva']);
  }

  // === Agenda ===
  abrirVerAgenda(): void {
    if (!this.asesorSeleccionado()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atención',
        detail: this.messages.SELECT_ADVISOR_FIRST
      });
      return;
    }

    const hoy = new Date();
    this.anioSeleccionado.set(hoy.getFullYear());
    this.mesSeleccionado.set(hoy.getMonth() + 1);
    this.semanaInicio.set(this.getInicioSemana(hoy));

    this.dialogAgendaVisible.set(true);
    this.cargarCitasSemana();
  }

  cerrarAgenda(): void {
    this.dialogAgendaVisible.set(false);
  }

  async cargarCitasSemana(): Promise<void> {
    const asesor = this.asesorSeleccionado();
    const dniCompleto =
      asesor?.dni ||
      (asesor?.cdtipo_identificacion && asesor?.nmidentificacion
        ? `${asesor.cdtipo_identificacion}${asesor.nmidentificacion}`
        : null);

    if (!dniCompleto) return;

    this.loadingAgenda.set(true);

    try {
      const inicio = this.semanaInicio();
      const finSemana = new Date(inicio);
      finSemana.setDate(finSemana.getDate() + 6);

      // Fecha local (no toISOString: convierte a UTC y corre el día en zonas UTC+)
      const fechaInicio = fechaLocal(inicio);
      const fechaFin = fechaLocal(finSemana);

      const citas = await this.agendaRepository.consultarCitasRangoByDni(
        dniCompleto,
        fechaInicio,
        fechaFin
      );

      this.citasSemana.set(citas);
    } catch (error) {
      this.citasSemana.set([]);
      this.messageService.add({
        severity: 'warn',
        summary: 'Aviso',
        detail:
          'No se pudieron cargar las citas. El servicio podría no estar disponible.',
        life: 3000
      });
    } finally {
      this.loadingAgenda.set(false);
    }
  }

  // === Calendario ===
  getInicioSemana(fecha: Date): Date {
    const d = new Date(fecha);
    // La semana inicia el lunes (igual que el legacy): getDay() es 0 = domingo.
    const dia = (d.getDay() + 6) % 7;
    d.setDate(d.getDate() - dia);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  mesAnterior(): void {
    let mes = this.mesSeleccionado();
    let anio = this.anioSeleccionado();
    mes--;
    if (mes < 1) {
      mes = 12;
      anio--;
    }
    this.mesSeleccionado.set(mes);
    this.anioSeleccionado.set(anio);
    this.onMesAnioChange();
  }

  mesSiguiente(): void {
    let mes = this.mesSeleccionado();
    let anio = this.anioSeleccionado();
    mes++;
    if (mes > 12) {
      mes = 1;
      anio++;
    }
    this.mesSeleccionado.set(mes);
    this.anioSeleccionado.set(anio);
    this.onMesAnioChange();
  }

  onMesAnioChange(): void {
    const primerDia = new Date(
      this.anioSeleccionado(),
      this.mesSeleccionado() - 1,
      1
    );
    this.semanaInicio.set(this.getInicioSemana(primerDia));
    this.cargarCitasSemana();
  }

  seleccionarSemana(dia: number): void {
    const fecha = new Date(
      this.anioSeleccionado(),
      this.mesSeleccionado() - 1,
      dia
    );
    this.semanaInicio.set(this.getInicioSemana(fecha));
    this.cargarCitasSemana();
  }

  irSemanaAnterior(): void {
    const inicio = this.semanaInicio();
    const nueva = new Date(inicio);
    nueva.setDate(nueva.getDate() - 7);
    this.semanaInicio.set(nueva);
    this.mesSeleccionado.set(nueva.getMonth() + 1);
    this.anioSeleccionado.set(nueva.getFullYear());
    this.cargarCitasSemana();
  }

  irSemanaSiguiente(): void {
    const inicio = this.semanaInicio();
    const nueva = new Date(inicio);
    nueva.setDate(nueva.getDate() + 7);
    this.semanaInicio.set(nueva);
    this.mesSeleccionado.set(nueva.getMonth() + 1);
    this.anioSeleccionado.set(nueva.getFullYear());
    this.cargarCitasSemana();
  }

  irSemanaHoy(): void {
    const hoy = new Date();
    this.semanaInicio.set(this.getInicioSemana(hoy));
    this.mesSeleccionado.set(hoy.getMonth() + 1);
    this.anioSeleccionado.set(hoy.getFullYear());
    this.cargarCitasSemana();
  }

  esDiaEnSemanaSeleccionada(dia: number): boolean {
    const fecha = new Date(
      this.anioSeleccionado(),
      this.mesSeleccionado() - 1,
      dia
    );
    const inicio = this.semanaInicio();
    const finSemana = new Date(inicio);
    finSemana.setDate(finSemana.getDate() + 6);
    return fecha >= inicio && fecha <= finSemana;
  }

  esHoy(dia: number): boolean {
    const hoy = new Date();
    return (
      hoy.getDate() === dia &&
      hoy.getMonth() === this.mesSeleccionado() - 1 &&
      hoy.getFullYear() === this.anioSeleccionado()
    );
  }

  esHoyFecha(fechaStr: string): boolean {
    const hoy = new Date();
    // Usar formato local YYYY-MM-DD (no toISOString que convierte a UTC)
    const year = hoy.getFullYear();
    const month = String(hoy.getMonth() + 1).padStart(2, '0');
    const day = String(hoy.getDate()).padStart(2, '0');
    return fechaStr === `${year}-${month}-${day}`;
  }

  // === Citas en Calendario ===
  getCitasEnHora(fecha: string, hora: number): ProgramacionServicio[] {
    return this.citasSemana().filter((cita) => {
      const citaFecha = cita.feprogramacion?.split('T')[0];
      const citaHora = parseInt(
        cita.feprogramacion?.split('T')[1]?.split(':')[0] || '0'
      );
      return citaFecha === fecha && citaHora === hora;
    });
  }

  /** Obtiene citas que INICIAN en un slot específico de 15 minutos */
  getCitasEnSlot(
    fecha: string,
    hora: number,
    minuto: number
  ): ProgramacionServicio[] {
    return this.citasSemana().filter((cita) => {
      if (!cita.feprogramacion) {
        return false;
      }
      const citaFecha = cita.feprogramacion.split('T')[0];
      if (citaFecha !== fecha) {
        return false;
      }
      const timeParts = cita.feprogramacion.split('T')[1]?.split(':');
      const citaHora = parseInt(timeParts?.[0] || '0');
      const citaMinuto = Math.floor(parseInt(timeParts?.[1] || '0') / 15) * 15;
      return citaHora === hora && citaMinuto === minuto;
    });
  }

  hayCitaOcupandoHora(
    fecha: string,
    hora: number
  ): ProgramacionServicio | null {
    return (
      this.citasSemana().find((cita) => {
        const citaFecha = cita.feprogramacion?.split('T')[0];
        if (citaFecha !== fecha) return false;

        const horaInicio = parseInt(
          cita.feprogramacion?.split('T')[1]?.split(':')[0] || '0'
        );
        const duracion = cita.nmhorasCita || 1;
        const horaFin = horaInicio + duracion;

        return hora >= horaInicio && hora < horaFin;
      }) || null
    );
  }

  /** Verifica si hay alguna cita ocupando un slot de 15 minutos */
  hayCitaOcupandoSlot(
    fecha: string,
    hora: number,
    minuto: number
  ): ProgramacionServicio | null {
    const slotMinutos = hora * 60 + minuto;
    return (
      this.citasSemana().find((cita) => {
        if (!cita.feprogramacion) {
          return false;
        }
        const citaFecha = cita.feprogramacion.split('T')[0];
        if (citaFecha !== fecha) {
          return false;
        }
        const timeParts = cita.feprogramacion.split('T')[1]?.split(':');
        const citaInicioMin =
          parseInt(timeParts?.[0] || '0') * 60 +
          parseInt(timeParts?.[1] || '0');
        const duracionMin = Math.round((cita.nmhorasCita || 1) * 60);
        return (
          slotMinutos >= citaInicioMin &&
          slotMinutos < citaInicioMin + duracionMin
        );
      }) || null
    );
  }

  /** Calcula altura de cita en px basada en slots de 15 min */
  getCitaHeightSlot(cita: ProgramacionServicio): number {
    const horas = cita.nmhorasCita || 1;
    return horas * 4 * this.SLOT_HEIGHT_PX - 2;
  }

  // === Formato de Citas ===
  getCitaTooltip(cita: ProgramacionServicio): string {
    const estado = cita.dsestado || cita.cdestado || 'Sin estado';
    const tipo = cita.tipoCita?.dstipo || cita.dstipoCita || 'Sin tipo';
    const obs = cita.dsobservaciones || '---';
    const SEPARADOR = ' - ';
    const CLICK_MSG = '(Haga click sobre la cita para modificarla)';

    if (cita.nmconsecOrdenServDetalle) {
      const clasificacion = cita.dsclasificacion || '';
      const cliente = cita.nombreBeneficiario?.trim() || '';
      const municipio = cita.dsmunicipioEntrega || '';
      const localidad = cita.dslocalidadPpal || cita.dslocalidad || '';
      const barrio = cita.dsbarrioPpal || cita.dsbarrio || '';
      const contacto = cita.dscontactoInfo?.trim() || cita.dscontacto || '';

      return (
        `Estado de la cita: ${estado}${SEPARADOR}` +
        `Clasificación: ${clasificacion}${SEPARADOR}` +
        `Cliente: ${cliente}${SEPARADOR}` +
        `Municipio de entrega: ${municipio}${SEPARADOR}` +
        `Localidad: ${localidad}${SEPARADOR}` +
        `Barrio: ${barrio}${SEPARADOR}` +
        `Contacto: ${contacto}   ${CLICK_MSG}`
      );
    }

    return (
      `Estado de la cita: ${estado}${SEPARADOR}` +
      `Tipo de cita: ${tipo}${SEPARADOR}` +
      `Nota: ${obs}   ${CLICK_MSG}`
    );
  }

  formatHoraCita(cita: ProgramacionServicio): string {
    if (!cita.feprogramacion) return '';
    const fecha = new Date(cita.feprogramacion);
    const duracionMinutos = Math.round((cita.nmhorasCita || 1) * 60);
    const horaFin = new Date(fecha.getTime() + duracionMinutos * 60000);

    const dia = fecha.getDate().toString().padStart(2, '0');
    const mes = (fecha.getMonth() + 1).toString().padStart(2, '0');
    const anio = fecha.getFullYear();
    const horaInicio =
      fecha.getHours().toString().padStart(2, '0') +
      ':' +
      fecha.getMinutes().toString().padStart(2, '0');
    const horaFinStr =
      horaFin.getHours().toString().padStart(2, '0') +
      ':' +
      horaFin.getMinutes().toString().padStart(2, '0');

    return `${dia}/${mes}/${anio} ${horaInicio}-${horaFinStr}`;
  }

  getLightColor(hexColor: string): string {
    if (!hexColor || !hexColor.startsWith('#')) return '#f5f5f5';
    return hexColor + '33';
  }

  getContrastTextColor(hexColor: string): string {
    if (!hexColor || !hexColor.startsWith('#')) return '#333';

    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#333' : '#fff';
  }

  getCitaColor(cita: ProgramacionServicio): string {
    const esEjecutada =
      cita.cdestado === 'EJE' ||
      cita.cdestado === '0016' ||
      cita.dsestado?.toUpperCase().includes('EJECUTA');

    if (cita.colorCita && cita.colorCita.startsWith('#')) {
      return cita.colorCita;
    }

    if (esEjecutada) {
      if (cita.dscolorCitaEjec && cita.dscolorCitaEjec.startsWith('#')) {
        return cita.dscolorCitaEjec;
      }
      if (
        cita.tipoCita?.dscolorCitaEjec &&
        cita.tipoCita.dscolorCitaEjec.startsWith('#')
      ) {
        return cita.tipoCita.dscolorCitaEjec;
      }
    } else {
      if (cita.dscolorCita && cita.dscolorCita.startsWith('#')) {
        return cita.dscolorCita;
      }
      if (
        cita.tipoCita?.dscolorCita &&
        cita.tipoCita.dscolorCita.startsWith('#')
      ) {
        return cita.tipoCita.dscolorCita;
      }
    }

    if (esEjecutada) return '#28a745';
    if (cita.cdestado === 'CAN' || cita.cdestado === '0017') return '#dc3545';
    return '#007bff';
  }

  // === Diálogo Cita ===
  async verDetalleCita(cita: ProgramacionServicio): Promise<void> {
    // Legacy: clickedPanel() - Verificar confidencialidad antes de abrir
    if (this.esCitaConfidencialBloqueada(cita)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Cita confidencial',
        detail: SCHEDULING_MESSAGES.CONFIDENTIAL_APPOINTMENT,
        life: 5000
      });
      return;
    }
    this.citaMode.set('edit');
    const asesor = this.asesorSeleccionado();
    const citaEnriquecida = {
      ...cita,
      cdpersonaAsesor: asesor?.cdpersona || 0
    };
    this.citaSeleccionada.set(citaEnriquecida);
    this.dialogDetalleCitaVisible.set(true);

    // Cargar detalle completo del API (el flag _periodicidadDialogoRespondido
    // en cita.facade evita que se muestre el diálogo de periodicidad dos veces)
    if (cita.nmconsecutivoProg) {
      this.loadingDetalle.set(true);
      try {
        const detalle = await this.agendaRepository.obtenerCita(
          cita.nmconsecutivoProg
        );
        this.citaSeleccionada.set({
          ...detalle,
          cdpersonaAsesor: asesor?.cdpersona || 0
        });
      } catch (error) {
        if (!environment.production) {
          console.error('Error cargando detalle de cita:', error);
        }
      } finally {
        this.loadingDetalle.set(false);
      }
    }
  }

  editarCita(cita: ProgramacionServicio): void {
    this.citaMode.set('edit');
    const asesor = this.asesorSeleccionado();
    const citaEnriquecida = {
      ...cita,
      cdpersonaAsesor: asesor?.cdpersona || 0
    };
    this.citaSeleccionada.set(citaEnriquecida);
    this.dialogDetalleCitaVisible.set(true);

    if (cita.nmconsecutivoProg) {
      this.loadingDetalle.set(true);
      this.agendaRepository
        .obtenerCita(cita.nmconsecutivoProg)
        .then((detalle) => {
          this.citaSeleccionada.set({
            ...detalle,
            cdpersonaAsesor: asesor?.cdpersona || 0
          });
        })
        .catch((error) => {
          if (!environment.production) {
            console.error('Error cargando detalle:', error);
          }
        })
        .finally(() => this.loadingDetalle.set(false));
    }
  }

  cerrarDialogoCita(): void {
    this.dialogDetalleCitaVisible.set(false);
    this.citaSeleccionada.set(null);
    this.citaMode.set('view');
  }

  /**
   * Verifica si una cita es confidencial y el usuario NO es el asesor asignado.
   * Legacy: SimpleAppointmentCustom.clickedPanel() - snconfidencial + comparar nmconsecutivo_asesor
   */
  private esCitaConfidencialBloqueada(cita: ProgramacionServicio): boolean {
    const tc = cita.tipoCita;
    if (!tc) {
      return false;
    }
    // El backend envía 'confidencial' (boolean) y/o 'snconfidencial' (string 'S'/'N')
    const esConfidencial =
      (tc as any).confidencial === true ||
      tc.snconfidencial?.toUpperCase() === 'S';
    if (!esConfidencial) {
      return false;
    }
    const usuarioConsecutivo = this.usuarioState.usuario()?.nmconsecutivo;
    return usuarioConsecutivo !== cita.nmconsecutivoAsesor;
  }

  async guardarCita(citaRequest: ProgramacionServicioRequest): Promise<boolean> {
    const citaActual = this.citaSeleccionada();
    const isCreate = this.citaMode() === 'create';

    this.loading.set(true);
    try {
      if (isCreate) {
        await this.agendaRepository.crearCita(citaRequest);
        this.messageService.add({
          severity: 'success',
          summary: 'Éxito',
          detail: this.messages.APPOINTMENT_CREATED
        });
      } else {
        const citaId = citaActual?.nmconsecutivoProg;
        if (citaId) {
          await this.agendaRepository.actualizarCita(citaId, citaRequest);
          this.messageService.add({
            severity: 'success',
            summary: 'Éxito',
            detail: this.messages.APPOINTMENT_UPDATED
          });
        } else {
          throw new Error('No se encontró el ID de la cita a actualizar');
        }
      }

      this.cerrarDialogoCita();
      await this.cargarCitasSemana();
      return true;
    } catch (error: unknown) {
      const errorMsg = extractHttpErrorMessage(error, 'No se pudo guardar la cita');

      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: errorMsg
      });
      return false;
    } finally {
      this.loading.set(false);
    }
  }

  async cancelarCita(cancelacion: CancelarCitaResult): Promise<void> {
    this.loading.set(true);
    try {
      await this.agendaRepository.cancelarCitaSP(cancelacion);

      this.messageService.add({
        severity: 'success',
        summary: 'Éxito',
        detail: this.messages.APPOINTMENT_CANCELLED
      });

      this.cerrarDialogoCita();
      await this.cargarCitasSemana();
    } catch (error: unknown) {
      // Extraer mensaje de error del backend (ovaMensajeUsuario del SP)
      let errorMsg = 'No se pudo cancelar la cita';
      if (error && typeof error === 'object' && 'error' in error) {
        const httpError = error as {
          error?: { message?: string; detail?: string };
        };
        errorMsg =
          httpError.error?.detail || httpError.error?.message || errorMsg;
      } else if (error instanceof Error) {
        errorMsg = error.message || errorMsg;
      }

      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: errorMsg
      });
    } finally {
      this.loading.set(false);
    }
  }

  async cumplirCita(citaId: number): Promise<void> {
    this.loading.set(true);
    try {
      // TODO: Implementar llamada al repository para cumplir
      // await this.agendaRepository.cumplirCita(citaId);

      this.messageService.add({
        severity: 'success',
        summary: 'Éxito',
        detail: 'Cita marcada como cumplida'
      });

      this.cerrarDialogoCita();
      await this.cargarCitasSemana();
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudo cumplir la cita'
      });
    } finally {
      this.loading.set(false);
    }
  }

  // === Formato Auxiliares ===
  formatFechaCita(fecha?: string): string {
    if (!fecha) return '-';
    try {
      const d = new Date(fecha);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}/${month}/${day}`;
    } catch {
      return fecha;
    }
  }

  formatHoraInicio(fecha?: string): string {
    if (!fecha) return '-';
    try {
      const d = new Date(fecha);
      const hours = String(d.getHours()).padStart(2, '0');
      const minutes = String(d.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    } catch {
      return '-';
    }
  }

  formatDuracion(horas?: number): string {
    if (!horas) return '-';
    const h = Math.floor(horas);
    const m = Math.round((horas - h) * 60);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }

  getActiveAccordionIndices(): number[] {
    const cita = this.citaSeleccionada();
    if (!cita) return [2];

    const estadosEjecutados = ['0016', 'EJE', '0028', 'PARCIAL', 'PEJ'];
    const esEjecutada = estadosEjecutados.some(
      (e) =>
        cita.cdestado?.toUpperCase() === e ||
        cita.dsestado?.toUpperCase().includes('EJECUTAD')
    );

    if (esEjecutada) {
      return [1, 2];
    } else {
      return [2];
    }
  }

  // === Crear Cita desde Calendario ===
  onCeldaCalendarioClick(fecha: string, hora: number, minuto = 0): void {
    const asesor = this.asesorSeleccionado();
    if (!asesor) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atención',
        detail: this.messages.SELECT_ADVISOR_FIRST
      });
      return;
    }

    const citaOcupando = this.hayCitaOcupandoSlot(fecha, hora, minuto);
    if (citaOcupando) {
      this.verDetalleCita(citaOcupando);
      return;
    }

    const dniAsesor =
      asesor?.dni ||
      (asesor?.cdtipo_identificacion && asesor?.nmidentificacion
        ? `${asesor.cdtipo_identificacion}${asesor.nmidentificacion}`
        : '');

    const citaNueva: Partial<ProgramacionServicio> = {
      feprogramacion: `${fecha}T${hora.toString().padStart(2, '0')}:${minuto.toString().padStart(2, '0')}:00`,
      nmhorasCita: 1,
      dniAsesor: dniAsesor,
      nmconsecutivoAsesor: 0,
      cdpersonaAsesor: asesor?.cdpersona || 0,
      nombreAsesor: getNombreCompleto(asesor),
      cdestado: 'PRG',
      dsestado: 'Programada',
      nmcantidadTotalProgramada: 1
    };

    this.citaMode.set('create');
    this.citaSeleccionada.set(citaNueva as ProgramacionServicio);
    this.dialogDetalleCitaVisible.set(true);
  }
}
